import { router } from '@inertiajs/vue3';
import { useWebNotification } from '@vueuse/core';
import { ensureEcho } from '@/lib/echo';
import { useToast } from '@/composables/useToast';
import { useSoundEffects } from '@/composables/useSoundEffects';
import { show as showSession } from '@/routes/sessions';
import { dashboard } from '@/routes';

interface TeamSessionCreatedPayload {
    session: { slug: string; name: string };
    team: { name: string };
    actor: string;
}

interface TeamInvitedPayload {
    team: { id: string; name: string };
    invited_by: string;
}

interface TeamRemovedPayload {
    team: { id: string; name: string };
}

let started = false;

/**
 * App-wide listener (started once from app.ts for the authenticated user) that
 * surfaces a toast + distinct sound + OS notification when a teammate starts a
 * team session or invites the user to a team, wherever they are in the app.
 * Delivered on the per-user private channel `App.Models.User.{id}`.
 */
export function startTeamNotifications(userId: number): void {
    if (started) {
        return;
    }
    started = true;

    ensureEcho();
    const toast = useToast();
    const { playTeamSessionCreated, playTeamInvited } = useSoundEffects();
    const { isSupported, show, onClick } = useWebNotification({
        tag: 'pokor-team',
        renotify: true,
    });

    // A same-tag OS notification replaces the previous one, so only the most
    // recent is ever on screen — navigate there when the user clicks it.
    let pendingUrl: string | null = null;
    onClick(() => {
        window.focus();
        if (pendingUrl) {
            void router.visit(pendingUrl);
        }
    });

    const channel = window.Echo.private(`App.Models.User.${userId}`);

    channel.listen('.App\\Events\\TeamSessionCreated', (event: TeamSessionCreatedPayload) => {
        const url = showSession.url(event.session.slug);

        toast.add(
            `${event.actor} started “${event.session.name}” in ${event.team.name}`,
            'info',
            10000,
            { label: 'Open session', handler: () => void router.visit(url) },
        );

        playTeamSessionCreated();

        if (isSupported.value) {
            pendingUrl = url;
            void show({
                title: `New session in ${event.team.name}`,
                body: `${event.actor} started “${event.session.name}”`,
            });
        }
    });

    channel.listen('.App\\Events\\TeamInvited', (event: TeamInvitedPayload) => {
        const url = dashboard.url();

        toast.add(`${event.invited_by} invited you to ${event.team.name}`, 'info', 10000, {
            label: 'View invite',
            handler: () => void router.visit(url),
        });

        playTeamInvited();

        // Refresh the pending-invite list live if the user is on the dashboard.
        if (window.location.pathname === '/') {
            router.reload({ only: ['pendingInvites', 'teams'] });
        }

        if (isSupported.value) {
            pendingUrl = url;
            void show({
                title: 'Team invitation',
                body: `${event.invited_by} invited you to ${event.team.name}`,
            });
        }
    });

    channel.listen('.App\\Events\\TeamRemoved', (event: TeamRemovedPayload) => {
        toast.warning(`You were removed from ${event.team.name}.`);

        // Drop the team from the dashboard immediately if we're on it.
        if (window.location.pathname === '/') {
            router.reload({ only: ['teams', 'pendingInvites'] });
        }
    });
}
